import { CustomBlending, NormalBlending, OneFactor, OneMinusSrcAlphaFactor, ShaderMaterial } from "three";
import { assemble } from "./chunks.ts";

const vertexShader = /* glsl */ `
in vec3 aCenter;
in float aSize;
in float aAlpha;
in vec3 aColor;
// O tipo (0 fumaça, 1 chama, 2 magia, 3 poeira, 4 onda na água, 5 marca no chão) na parte inteira e a
// semente da instância na fração (F93).
in float aKind;
out vec2 vUv;
out float vAlpha;
out vec3 vColor;
out float vKind;
out float vSeed;

void main() {
  vUv = uv;
  vAlpha = aAlpha;
  vColor = aColor;
  vKind = floor(aKind);
  vSeed = fract(aKind);
  if (vKind > 3.5) {
    // Onda e marca: deitadas no plano (x, z) do ponto, giradas pela semente; não viram para a câmera. O quad
    // (no plano xy, de frente para +z) gira −90° em x, (x, y) → (x, 0, −y): a face fica para cima (com
    // (x, 0, y) ela virava para baixo e o descarte das costas a tirava vista de cima).
    float turn = vSeed * 6.2832;
    vec2 p = mat2(cos(turn), -sin(turn), sin(turn), cos(turn)) * position.xy * aSize;
    gl_Position = projectionMatrix * viewMatrix * vec4(aCenter + vec3(p.x, 0.0, -p.y), 1.0);
    return;
  }
  vec4 view = viewMatrix * vec4(aCenter, 1.0);
  if (vKind > 0.5 && vKind < 1.5) {
    // Chama: a língua é mais alta que larga e nasce no ponto (a base do quad no centro).
    view.xy += position.xy * aSize * vec2(0.8, 1.6);
    view.y += aSize * 0.8;
  } else view.xy += position.xy * aSize;
  gl_Position = projectionMatrix * view;
}
`;

const fragmentShader = /* glsl */ `
${assemble("fragment", ["hash", "noise"])}
uniform float uTime;
// Tremeluzir e cintilar (0 com "Reduzir lampejos") e o brilho das chamas e da magia (acima de 1, o bloom pega).
uniform float uFlicker;
uniform float uGlow;
// "Reduzir lampejos": as chamas e a magia cobrem o fundo em vez de somar luz (1) ou somam no miolo (0).
uniform float uOver;
in vec2 vUv;
in float vAlpha;
in vec3 vColor;
in float vKind;
in float vSeed;

void main() {
  // Chamas e magia saem pré-multiplicadas (a mistura do material delas é 1, 1 − alfa): o alfa diz quanto do
  // fundo some, e a cor já vem vezes a cobertura, aplicada depois da conversão de cor da saída.
  float cover = 1.0;
  if (vKind > 3.5) {
    float d = length(vUv - 0.5) * 2.0;
    float a;
    vec3 color = vColor;
    if (vKind < 4.5) {
      // Onda: um anel fino na borda e outro mais fraco por dentro, que abrem juntos (o tamanho cresce).
      a = (1.0 - smoothstep(0.0, 0.07, abs(d - 0.88))) + 0.45 * (1.0 - smoothstep(0.0, 0.06, abs(d - 0.55)));
    } else {
      // Marca de lama: uma mancha de borda irregular (ruído pelo ângulo e pela semente), escura no meio, com o
      // rebordo da lama espremida mais claro (o brilho molhado): no mangue escuro do entardecer, só o escuro sumia.
      vec2 c = vUv - 0.5;
      float edge = 0.62 + 0.28 * noise(vec2(atan(c.y, c.x) * 1.6 + vSeed * 40.0, vSeed * 13.0));
      float inside = 1.0 - smoothstep(edge - 0.18, edge, d);
      float rim = smoothstep(edge - 0.34, edge - 0.14, d) * inside;
      color = mix(vColor, vec3(0.2), rim * 0.85);
      a = inside * (0.75 + 0.25 * (1.0 - d));
    }
    gl_FragColor = vec4(color, a * vAlpha);
  } else if (vKind < 0.5 || vKind > 2.5) {
    // Fumaça e poeira (Fase 14, tipo 3): o disco suave de sempre, na cor da instância.
    float d = length(vUv - 0.5) * 2.0;
    float a = (1.0 - smoothstep(0.4, 1.0, d)) * vAlpha;
    gl_FragColor = vec4(vColor, a);
  } else if (vKind < 1.5) {
    // Chama: a língua afina para cima, balança e se rasga pelo ruído que sobe; o miolo quente fica claro.
    float t = uTime * uFlicker;
    float s = vSeed * 37.0;
    vec2 q = vUv;
    float n = noise(vec2(q.x * 3.0 + s, q.y * 2.6 - t * 3.4 + s)) * 0.7 + noise(vec2(q.x * 6.5 - s, q.y * 5.0 - t * 5.2)) * 0.3;
    float sway = (noise(vec2(s, t * 1.7)) - 0.5) * 0.35 * q.y;
    float x = abs(q.x - 0.5 - sway - (n - 0.5) * 0.18 * q.y);
    float width = 0.5 * pow(1.0 - q.y, 0.5);
    float body = (1.0 - smoothstep(width * 0.7, width, x)) * smoothstep(0.0, 0.12, q.y);
    float tongue = smoothstep(0.18, 0.34, (1.0 - q.y) * 1.1 + (n - 0.5) * 1.2);
    float a = body * tongue;
    // O miolo quente só embaixo e no meio (peso 0,6); a língua fica na cor da instância, sem passar de 1.
    float core = 0.6 * smoothstep(0.55, 0.95, a * (1.0 - q.y) * (1.0 - 2.4 * x));
    vec3 color = mix(vColor, vec3(1.0, 0.92, 0.72) * uGlow, core);
    // A língua cobre o fundo na cor dela (85 %) e só o miolo soma luz: sobre o chão claro do dia, a chama fica
    // laranja e não desbota para o salmão (o verde do fundo somado ao laranja clareava tudo). Reduzido, cobre
    // por inteiro (sem somar luz, sem bloom).
    cover = a * vAlpha;
    gl_FragColor = vec4(color, cover * mix(0.85 * (1.0 - core / 0.6), 1.0, uOver));
  } else {
    // Magia: uma estrela de quatro pontas girando, com outra menor em diagonal e o miolo branco que cintila.
    float s = vSeed * 6.2832;
    float spin = s + uTime * uFlicker * 2.5;
    vec2 c = (vUv - 0.5) * 2.0;
    vec2 r = mat2(cos(spin), -sin(spin), sin(spin), cos(spin)) * c;
    vec2 d = mat2(0.7071, -0.7071, 0.7071, 0.7071) * r;
    float len = length(c);
    float star = pow(max(0.0, 1.0 - abs(r.x * r.y) * 28.0), 6.0) + 0.5 * pow(max(0.0, 1.0 - abs(d.x * d.y) * 60.0), 8.0);
    float core = 1.0 - smoothstep(0.0, 0.28, len);
    float twinkle = mix(1.0, 0.55 + 0.45 * sin(uTime * 16.0 + s * 7.0), uFlicker);
    float a = clamp(star * (1.0 - smoothstep(0.35, 1.0, len)) + core, 0.0, 1.0) * twinkle;
    vec3 color = mix(vColor, vec3(1.0), core) * uGlow;
    // A faísca só soma luz (alfa 0); reduzida, cobre o fundo como a chama.
    cover = a * vAlpha;
    gl_FragColor = vec4(color, cover * uOver);
  }
  #include <colorspace_fragment>
  gl_FragColor.rgb *= cover;
}
`;

/** Brilho das chamas e da magia: o miolo passa de 1,0 e o bloom pega (ADR 0014: só efeito passa de 1). */
export const FLAME_GLOW = 2.2;
/** Brilho com "Reduzir lampejos": nenhum canal passa de 1 e a mistura só cobre o fundo, então nada chega ao bloom. */
export const FLAME_GLOW_REDUCED = 1;

/**
 * Fumaça (assombração se desfazendo ao amanhecer), chamas e magia (F93): quads instanciados virados para a
 * câmera, com a cor e o tipo por instância. Um programa só: a fumaça mistura normal, e as chamas e a magia
 * (`glow`) vão num segundo material com a mesma fonte e a mistura pré-multiplicada (1, 1 − alfa), com o
 * `premultipliedAlpha` falso. O three r186 não põe o `blending` nem os fatores na chave do programa (só o
 * `opaque`, falso com `transparent`, e o `premultipliedAlpha`), então os dois dividem o programa `fumaca`.
 */
export function createSmokeMaterial(glow = false): ShaderMaterial {
  const material = new ShaderMaterial({
    name: "fumaca",
    uniforms: { uTime: { value: 0 }, uFlicker: { value: 1 }, uGlow: { value: FLAME_GLOW }, uOver: { value: 0 } },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    blending: glow ? CustomBlending : NormalBlending,
  });
  if (glow) {
    material.blendSrc = OneFactor;
    material.blendDst = OneMinusSrcAlphaFactor;
  }
  return material;
}
